import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReportPreviewMetricCharts from 'app/modules/analytics/modules/reports/components/reportPreview/ReportPreviewMetricCharts';
import ReportPreviewSectionBlock from 'app/modules/analytics/modules/reports/components/reportPreview/ReportPreviewSectionBlock';
import ReportPreviewSectionNote from 'app/modules/analytics/modules/reports/components/reportPreview/ReportPreviewSectionNote';
import ReportPreviewTopPostsSection from 'app/modules/analytics/modules/reports/components/reportPreview/ReportPreviewTopPostsSection';
import ReportPlatformBlocks from 'app/modules/analytics/modules/reports/components/reportPreview/ReportPlatformBlocks';
import ReportPlatformMetricRows from 'app/modules/analytics/modules/reports/components/reportPreview/ReportPlatformMetricRows';
import type { ReportMetricRowsConfig } from 'app/modules/analytics/modules/reports/constants/reportMetricRowDefinitions';
import {
  getUncoveredReportMetricDefinitions,
  resolvePageScopedReportMetrics,
  rowHasVisibleMetrics,
} from 'app/modules/analytics/modules/reports/utils/resolvePageScopedReportMetrics';
import type { ReportProfilePreviewSectionsProps } from '../types/ReportProfilePreviewSectionsProps';

interface PlatformReportPreviewConfig {
  performanceRows?: ReportMetricRowsConfig[];
  contentRows?: ReportMetricRowsConfig[];
}

interface Props extends ReportProfilePreviewSectionsProps {
  config: PlatformReportPreviewConfig;
}

const PlatformReportPreviewSections = ({
  page,
  performanceMetrics,
  contentMetrics,
  performancePages,
  performanceOverview,
  isPerformanceLoading,
  getPerformanceMetricValue,
  performanceEmptyMessage,
  isContentLoading,
  getContentMetricValue,
  contentPosts,
  isContentPostsLoading = false,
  performanceNote,
  contentNote,
  showPerformanceNote = false,
  showContentNote = false,
  config,
}: Props) => {
  const { t } = useTranslation();

  const performanceRowConfigs = useMemo(
    () => config.performanceRows ?? [],
    [config.performanceRows],
  );
  const contentRowConfigs = useMemo(
    () => config.contentRows ?? [],
    [config.contentRows],
  );

  const { pagePerformanceTileMetrics, pageContentTileMetrics } = useMemo(
    () =>
      resolvePageScopedReportMetrics(page, performanceMetrics, contentMetrics),
    [contentMetrics, page, performanceMetrics],
  );

  const platformBlocks = page.platformBlocks ?? [];
  const contentTableBlocks = platformBlocks.filter((block) =>
    block.kind.includes('content'),
  );
  const performancePlatformBlocks = platformBlocks.filter(
    (block) => !block.kind.includes('content'),
  );

  const performanceOverflowDefinitions = useMemo(
    () =>
      getUncoveredReportMetricDefinitions(
        performanceRowConfigs,
        pagePerformanceTileMetrics,
        'performance',
      ),
    [pagePerformanceTileMetrics, performanceRowConfigs],
  );

  const contentOverflowDefinitions = useMemo(
    () =>
      getUncoveredReportMetricDefinitions(
        contentRowConfigs,
        pageContentTileMetrics,
        'content',
      ),
    [contentRowConfigs, pageContentTileMetrics],
  );

  const hasPerformanceMetrics =
    page.chartMetrics.length > 0 ||
    performancePlatformBlocks.length > 0 ||
    pagePerformanceTileMetrics.length > 0;

  const hasContentMetrics =
    pageContentTileMetrics.length > 0 ||
    contentTableBlocks.length > 0 ||
    contentRowConfigs.some((row) =>
      rowHasVisibleMetrics(row.definitions, pageContentTileMetrics),
    );

  return (
    <div className="tw-space-y-6">
      {hasPerformanceMetrics ? (
        <ReportPreviewSectionBlock
          title={t('analyticsReports.createReport.previewPerformance')}
        >
          {performanceRowConfigs.map((row, index) => (
            <ReportPlatformMetricRows
              key={`performance-row-${index}`}
              rowDefinitions={row.definitions}
              selectedMetrics={pagePerformanceTileMetrics}
              overview={performanceOverview}
              isLoading={isPerformanceLoading}
              maxColumns={row.maxColumns}
            />
          ))}
          {performanceOverflowDefinitions.length > 0 ? (
            <ReportPlatformMetricRows
              rowDefinitions={performanceOverflowDefinitions}
              selectedMetrics={pagePerformanceTileMetrics}
              overview={performanceOverview}
              isLoading={isPerformanceLoading}
              maxColumns={4}
            />
          ) : null}
          {performancePlatformBlocks.length > 0 ? (
            <ReportPlatformBlocks
              blocks={performancePlatformBlocks}
              performanceOverview={performanceOverview}
              contentPosts={contentPosts}
              isPerformanceLoading={isPerformanceLoading}
              isContentLoading={isContentLoading}
              selectedPerformanceMetrics={performanceMetrics}
            />
          ) : null}
          {page.chartMetrics.length > 0 ? (
            <ReportPreviewMetricCharts
              chartMetrics={page.chartMetrics}
              pages={performancePages}
              overview={performanceOverview}
              isLoading={isPerformanceLoading}
            />
          ) : null}
        </ReportPreviewSectionBlock>
      ) : null}
      {showPerformanceNote ? (
        <ReportPreviewSectionBlock
          title={t('analyticsReports.createReport.previewPerformance')}
        >
          <ReportPreviewSectionNote note={performanceNote} />
        </ReportPreviewSectionBlock>
      ) : null}
      {hasContentMetrics ? (
        <ReportPreviewSectionBlock
          title={t('analyticsReports.createReport.previewContent')}
        >
          {contentRowConfigs.map((row, index) => (
            <ReportPlatformMetricRows
              key={`content-row-${index}`}
              rowDefinitions={row.definitions}
              selectedMetrics={pageContentTileMetrics}
              overview={performanceOverview}
              getContentMetricValue={getContentMetricValue}
              isLoading={isContentLoading}
              maxColumns={row.maxColumns}
            />
          ))}
          {contentOverflowDefinitions.length > 0 ? (
            <ReportPlatformMetricRows
              rowDefinitions={contentOverflowDefinitions}
              selectedMetrics={pageContentTileMetrics}
              overview={performanceOverview}
              getContentMetricValue={getContentMetricValue}
              isLoading={isContentLoading}
              maxColumns={4}
            />
          ) : null}
          {contentTableBlocks.length > 0 ? (
            <ReportPlatformBlocks
              blocks={contentTableBlocks}
              performanceOverview={performanceOverview}
              contentPosts={contentPosts}
              isPerformanceLoading={isPerformanceLoading}
              isContentLoading={isContentLoading}
            />
          ) : null}
        </ReportPreviewSectionBlock>
      ) : null}
      {showContentNote ? (
        <ReportPreviewSectionBlock
          title={t('analyticsReports.createReport.previewContent')}
        >
          <ReportPreviewSectionNote note={contentNote} />
        </ReportPreviewSectionBlock>
      ) : null}
      {(page.postChunk?.length ?? 0) > 0 ? (
        <ReportPreviewTopPostsSection
          posts={page.postChunk ?? []}
          isLoading={isContentPostsLoading}
        />
      ) : null}
    </div>
  );
};

export default PlatformReportPreviewSections;
